<template>
  <component
    :is="tag"
    v-if="embed"
    ref="maveElement"
    v-bind="staticAttrs"
  />
</template>

<script setup lang="ts">
import type { MaveQuality, MaveTag } from '~/utils/mave';

type MaveElement = HTMLElement & {
  play(): void;
  pause(): void;
  muted: boolean;
  paused: boolean;
};

const emit = defineEmits<{
  play: [];
  pause: [];
  mutedchange: [muted: boolean];
}>();

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<{
  tag: MaveTag;
  embed: string;
  quality?: MaveQuality;
  autoplay?: boolean;
  loop?: boolean;
  controls?: string;
  muted?: boolean;
  poster?: string;
}>(), {
  quality: 'uhd',
  autoplay: undefined,
  loop: undefined,
  controls: undefined,
  muted: undefined,
  poster: undefined,
});

const attrs = useAttrs();
const maveElement = ref<MaveElement>();

const onNativePlay = () => emit('play');
const onNativePause = () => emit('pause');
const onNativeVolumeChange = () => {
  emit('mutedchange', maveElement.value?.muted ?? true);
};

const staticAttrs = computed(() => {
  const result: Record<string, string | boolean | EventListener> = {};
  const styles: string[] = [];

  Object.entries(attrs).forEach(([key, value]) => {
    if (key === 'style' && typeof value === 'string') {
      styles.push(value);
      return;
    }

    if (key.startsWith('on') && typeof value === 'function') {
      result[key] = value as EventListener;
      return;
    }

    if (typeof value === 'string' || typeof value === 'boolean') {
      result[key] = value;
    }
  });

  const style = styles.filter(Boolean).join(' ');

  if (style) {
    result.style = style;
  }

  return result;
});

const applyMaveAttributes = async () => {
  if (!maveElement.value) {
    return;
  }

  const element = maveElement.value;

  await customElements.whenDefined(props.tag);

  element.setAttribute('embed', props.embed);
  element.setAttribute('quality', props.quality);

  element.removeEventListener('play', onNativePlay);
  element.removeEventListener('pause', onNativePause);
  element.removeEventListener('volumechange', onNativeVolumeChange);
  element.addEventListener('play', onNativePlay);
  element.addEventListener('pause', onNativePause);
  element.addEventListener('volumechange', onNativeVolumeChange);

  if (props.autoplay) {
    element.setAttribute('autoplay', 'always');
  } else {
    element.removeAttribute('autoplay');
  }

  if (props.controls) {
    element.setAttribute('controls', props.controls);
  } else {
    element.removeAttribute('controls');
  }

  if (props.loop) {
    element.setAttribute('loop', '');
  } else {
    element.removeAttribute('loop');
  }

  if (props.tag === 'mave-player') {
    if (props.poster) {
      element.setAttribute('poster', props.poster);
    } else {
      element.removeAttribute('poster');
    }

    if (props.muted !== undefined) {
      element.muted = props.muted;
    }
  }

  if (props.tag === 'mave-clip' && props.poster) {
    element.setAttribute('fallback', 'thumbnail');
  }

  if (props.autoplay && element.paused) {
    try {
      await element.play();
    } catch {
      // Browser may block until user interaction if not muted.
    }
  }
};

watch(
  [
    maveElement,
    () => props.tag,
    () => props.embed,
    () => props.quality,
    () => props.autoplay,
    () => props.controls,
    () => props.loop,
    () => props.muted,
    () => props.poster,
  ],
  () => {
    applyMaveAttributes();
  },
  { flush: 'post' },
);

onUnmounted(() => {
  maveElement.value?.removeEventListener('play', onNativePlay);
  maveElement.value?.removeEventListener('pause', onNativePause);
  maveElement.value?.removeEventListener('volumechange', onNativeVolumeChange);
});

defineExpose({
  play: () => maveElement.value?.play(),
  pause: () => maveElement.value?.pause(),
  get muted() {
    return maveElement.value?.muted ?? true;
  },
  set muted(value: boolean) {
    if (maveElement.value) {
      maveElement.value.muted = value;
    }
  },
  get paused() {
    return maveElement.value?.paused ?? true;
  },
});
</script>
